/**
 * Estimation engine — a pure, side-effect-free evaluator.
 *
 * Per our architecture the estimation math runs in the BROWSER: this module
 * takes a {@link Template} (its {@link FieldDef}s + {@link OutputDef}
 * expressions) plus the user's inputs and computes the output values. The
 * backend only stores the resulting {@link Estimate} document.
 *
 * It supports the template's configurable field kinds (base numbers, factors,
 * multipliers, thresholds, enums, text, booleans) and evaluates each output's
 * expression against a scope built from the field KEYS (plus any earlier
 * outputs, so an output can reference a previously-computed output).
 *
 * SAFETY: expressions are evaluated by a hand-written recursive-descent parser
 * over a fixed grammar and a fixed function whitelist. It NEVER uses `eval` or
 * `new Function`, so a template author cannot execute arbitrary code. Any parse
 * or evaluation problem is captured per-output as an error string rather than
 * thrown to the caller.
 *
 * Pure TS — no React, no I/O, no globals mutated.
 */

import { isNumericFieldType } from '../templates/templateModel';
import type { FieldDef, OutputDef, Template } from '../templates/types';
import type {
  EstimateInputs,
  EstimateInputValue,
  EstimateOutputs,
  EstimateOutputValue,
} from './types';

/** A value the expression grammar can produce or operate on. */
export type EvalValue = number | boolean | string;

/** Result of evaluating a whole template against inputs. */
export interface EvaluationResult {
  /** Computed output values keyed by output key (only successful outputs). */
  outputs: EstimateOutputs;
  /** Per-output human-readable error messages (empty when all succeeded). */
  errors: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Input coercion
// ---------------------------------------------------------------------------

/**
 * Coerces a raw editor string into the correctly-typed value for a field.
 * Numeric field kinds parse to a finite number (falling back to 0), booleans to
 * true/false, and enum/text to the trimmed string.
 */
export function coerceInputValue(
  field: FieldDef,
  raw: string | number | boolean | undefined,
): EstimateInputValue {
  if (isNumericFieldType(field.type)) {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    if (typeof raw === 'boolean') return raw ? 1 : 0;
    const n = Number(String(raw ?? '').trim());
    return Number.isFinite(n) ? n : 0;
  }
  if (field.type === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    const s = String(raw ?? '').trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  // enum / text
  if (raw === undefined || raw === null) return '';
  return String(raw);
}

/**
 * Builds a fully-populated inputs map for a template, using each field's
 * defaultValue where present (coerced) and a type-appropriate zero-value
 * otherwise. Guarantees every field key is present.
 */
export function defaultInputs(template: Template): EstimateInputs {
  const inputs: EstimateInputs = {};
  for (const field of template.fields) {
    if (field.defaultValue !== undefined) {
      inputs[field.key] = coerceInputValue(field, field.defaultValue);
    } else if (isNumericFieldType(field.type)) {
      inputs[field.key] = 0;
    } else if (field.type === 'boolean') {
      inputs[field.key] = false;
    } else if (field.type === 'enum') {
      inputs[field.key] = field.options?.[0] ?? '';
    } else {
      inputs[field.key] = '';
    }
  }
  return inputs;
}

/**
 * Merges partial user inputs over the template defaults, coercing every value
 * to its field's type and dropping keys that are not in the template schema.
 */
export function normalizeInputs(
  template: Template,
  raw: Partial<Record<string, EstimateInputValue>>,
): EstimateInputs {
  const inputs = defaultInputs(template);
  for (const field of template.fields) {
    const value = raw[field.key];
    if (value !== undefined) {
      inputs[field.key] = coerceInputValue(field, value);
    }
  }
  return inputs;
}

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

/**
 * Builds the identifier scope for expression evaluation from the template's
 * fields and the (already coerced) inputs. Text/enum values remain strings;
 * booleans remain booleans; numeric fields remain numbers.
 */
function buildScope(
  template: Template,
  inputs: EstimateInputs,
): Record<string, EvalValue> {
  const scope: Record<string, EvalValue> = {};
  for (const field of template.fields) {
    const value = inputs[field.key];
    scope[field.key] = value === undefined ? 0 : value;
  }
  return scope;
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type TokenType = 'number' | 'string' | 'ident' | 'op' | 'eof';

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

const MULTI_CHAR_OPS = ['<=', '>=', '==', '!=', '&&', '||'];
const SINGLE_CHAR_OPS = new Set([
  '+', '-', '*', '/', '%', '(', ')', ',', '<', '>', '!', '?', ':',
]);

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i];

    // Whitespace
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // Number (int or decimal)
    if ((ch >= '0' && ch <= '9') || (ch === '.' && /[0-9]/.test(input[i + 1] ?? ''))) {
      let j = i + 1;
      while (j < n && /[0-9.]/.test(input[j])) j++;
      const value = input.slice(i, j);
      if ((value.match(/\./g) ?? []).length > 1) {
        throw new Error(`Malformed number "${value}" at position ${i}`);
      }
      tokens.push({ type: 'number', value, pos: i });
      i = j;
      continue;
    }

    // String literal ('...' or "...")
    if (ch === "'" || ch === '"') {
      const quote = ch;
      let j = i + 1;
      let str = '';
      while (j < n && input[j] !== quote) {
        if (input[j] === '\\' && j + 1 < n) {
          str += input[j + 1];
          j += 2;
        } else {
          str += input[j];
          j++;
        }
      }
      if (j >= n) throw new Error(`Unterminated string starting at position ${i}`);
      tokens.push({ type: 'string', value: str, pos: i });
      i = j + 1;
      continue;
    }

    // Identifier / keyword
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_]/.test(input[j])) j++;
      tokens.push({ type: 'ident', value: input.slice(i, j), pos: i });
      i = j;
      continue;
    }

    // Multi-char operator
    const two = input.slice(i, i + 2);
    if (MULTI_CHAR_OPS.includes(two)) {
      tokens.push({ type: 'op', value: two, pos: i });
      i += 2;
      continue;
    }

    // Single-char operator
    if (SINGLE_CHAR_OPS.has(ch)) {
      tokens.push({ type: 'op', value: ch, pos: i });
      i++;
      continue;
    }

    throw new Error(`Unexpected character "${ch}" at position ${i}`);
  }

  tokens.push({ type: 'eof', value: '', pos: n });
  return tokens;
}

// ---------------------------------------------------------------------------
// Function whitelist
// ---------------------------------------------------------------------------

type EvalFn = (args: EvalValue[]) => EvalValue;

function toNum(v: EvalValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

const FUNCTIONS: Record<string, EvalFn> = {
  min: (a) => Math.min(...a.map(toNum)),
  max: (a) => Math.max(...a.map(toNum)),
  abs: (a) => Math.abs(toNum(a[0])),
  round: (a) => {
    const digits = a[1] === undefined ? 0 : Math.trunc(toNum(a[1]));
    const f = Math.pow(10, digits);
    return Math.round(toNum(a[0]) * f) / f;
  },
  floor: (a) => Math.floor(toNum(a[0])),
  ceil: (a) => Math.ceil(toNum(a[0])),
  sqrt: (a) => Math.sqrt(toNum(a[0])),
  pow: (a) => Math.pow(toNum(a[0]), toNum(a[1])),
  clamp: (a) => Math.min(Math.max(toNum(a[0]), toNum(a[1])), toNum(a[2])),
  if: (a) => (truthy(a[0]) ? a[1] : a[2]),
};

function truthy(v: EvalValue): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  return v !== '';
}

// ---------------------------------------------------------------------------
// Parser (recursive descent) + evaluator
// ---------------------------------------------------------------------------

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly scope: Record<string, EvalValue>,
  ) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private next(): Token {
    return this.tokens[this.pos++];
  }

  private expect(value: string): void {
    const t = this.peek();
    if (t.type === 'op' && t.value === value) {
      this.pos++;
      return;
    }
    throw new Error(`Expected "${value}" but found "${t.value || 'end of input'}"`);
  }

  /** Entry point: parse a full expression and assert nothing trails. */
  parse(): EvalValue {
    const value = this.parseTernary();
    if (this.peek().type !== 'eof') {
      throw new Error(`Unexpected trailing token "${this.peek().value}"`);
    }
    return value;
  }

  private isOp(value: string): boolean {
    const t = this.peek();
    return t.type === 'op' && t.value === value;
  }

  // ternary: logicalOr ('?' ternary ':' ternary)?
  private parseTernary(): EvalValue {
    const cond = this.parseOr();
    if (this.isOp('?')) {
      this.next();
      const whenTrue = this.parseTernary();
      this.expect(':');
      const whenFalse = this.parseTernary();
      return truthy(cond) ? whenTrue : whenFalse;
    }
    return cond;
  }

  private parseOr(): EvalValue {
    let left = this.parseAnd();
    while (this.isOp('||')) {
      this.next();
      const right = this.parseAnd();
      left = truthy(left) || truthy(right);
    }
    return left;
  }

  private parseAnd(): EvalValue {
    let left = this.parseEquality();
    while (this.isOp('&&')) {
      this.next();
      const right = this.parseEquality();
      left = truthy(left) && truthy(right);
    }
    return left;
  }

  private parseEquality(): EvalValue {
    let left = this.parseComparison();
    while (this.isOp('==') || this.isOp('!=')) {
      const op = this.next().value;
      const right = this.parseComparison();
      const eq = looseEquals(left, right);
      left = op === '==' ? eq : !eq;
    }
    return left;
  }

  private parseComparison(): EvalValue {
    let left = this.parseAdditive();
    while (
      this.isOp('<') ||
      this.isOp('>') ||
      this.isOp('<=') ||
      this.isOp('>=')
    ) {
      const op = this.next().value;
      const right = this.parseAdditive();
      const l = toNum(left);
      const r = toNum(right);
      switch (op) {
        case '<': left = l < r; break;
        case '>': left = l > r; break;
        case '<=': left = l <= r; break;
        default: left = l >= r; break;
      }
    }
    return left;
  }

  private parseAdditive(): EvalValue {
    let left = this.parseMultiplicative();
    while (this.isOp('+') || this.isOp('-')) {
      const op = this.next().value;
      const right = this.parseMultiplicative();
      if (op === '+') {
        // Numeric addition; string concat only if either side is a string.
        if (typeof left === 'string' || typeof right === 'string') {
          left = `${stringify(left)}${stringify(right)}`;
        } else {
          left = toNum(left) + toNum(right);
        }
      } else {
        left = toNum(left) - toNum(right);
      }
    }
    return left;
  }

  private parseMultiplicative(): EvalValue {
    let left = this.parseUnary();
    while (this.isOp('*') || this.isOp('/') || this.isOp('%')) {
      const op = this.next().value;
      const right = this.parseUnary();
      const l = toNum(left);
      const r = toNum(right);
      switch (op) {
        case '*': left = l * r; break;
        case '/': left = r === 0 ? NaN : l / r; break;
        default: left = r === 0 ? NaN : l % r; break;
      }
    }
    return left;
  }

  private parseUnary(): EvalValue {
    if (this.isOp('-')) {
      this.next();
      return -toNum(this.parseUnary());
    }
    if (this.isOp('+')) {
      this.next();
      return toNum(this.parseUnary());
    }
    if (this.isOp('!')) {
      this.next();
      return !truthy(this.parseUnary());
    }
    return this.parsePrimary();
  }

  private parsePrimary(): EvalValue {
    const t = this.peek();

    if (t.type === 'number') {
      this.next();
      return Number(t.value);
    }

    if (t.type === 'string') {
      this.next();
      return t.value;
    }

    if (this.isOp('(')) {
      this.next();
      const value = this.parseTernary();
      this.expect(')');
      return value;
    }

    if (t.type === 'ident') {
      this.next();
      // Keyword literals
      if (t.value === 'true') return true;
      if (t.value === 'false') return false;

      // Function call
      if (this.isOp('(')) {
        const fn = FUNCTIONS[t.value];
        if (!fn) throw new Error(`Unknown function "${t.value}"`);
        this.next(); // consume '('
        const args: EvalValue[] = [];
        if (!this.isOp(')')) {
          args.push(this.parseTernary());
          while (this.isOp(',')) {
            this.next();
            args.push(this.parseTernary());
          }
        }
        this.expect(')');
        return fn(args);
      }

      // Identifier reference
      if (Object.prototype.hasOwnProperty.call(this.scope, t.value)) {
        return this.scope[t.value];
      }
      throw new Error(`Unknown identifier "${t.value}"`);
    }

    throw new Error(`Unexpected token "${t.value || 'end of input'}"`);
  }
}

function stringify(v: EvalValue): string {
  return typeof v === 'string' ? v : String(v);
}

function looseEquals(a: EvalValue, b: EvalValue): boolean {
  if (typeof a === 'string' || typeof b === 'string') {
    return stringify(a) === stringify(b);
  }
  return toNum(a) === toNum(b);
}

/**
 * Safely evaluates a single expression against a scope. Returns the value or
 * throws an Error with a human-readable message (callers capture it per-output).
 */
export function evaluateExpression(
  expression: string,
  scope: Record<string, EvalValue>,
): EvalValue {
  const tokens = tokenize(expression);
  return new Parser(tokens, scope).parse();
}

// ---------------------------------------------------------------------------
// Template evaluation
// ---------------------------------------------------------------------------

/** Normalizes an eval result into a storable output value. */
function toOutputValue(v: EvalValue): EstimateOutputValue {
  if (typeof v === 'number') {
    // Guard against NaN/Infinity leaking into stored documents.
    return Number.isFinite(v) ? v : NaN;
  }
  return v;
}

/**
 * Evaluates every output expression of a template against the given inputs.
 *
 * Outputs are evaluated in declaration order and each successfully-computed
 * output is added to the scope, so a later output may reference an earlier one
 * by key. A failing output is recorded in {@link EvaluationResult.errors} and
 * omitted from {@link EvaluationResult.outputs}; other outputs still compute.
 */
export function evaluateTemplate(
  template: Template,
  inputs: EstimateInputs,
): EvaluationResult {
  const scope = buildScope(template, inputs);
  const outputs: EstimateOutputs = {};
  const errors: Record<string, string> = {};

  const defs: OutputDef[] = template.outputs ?? [];
  for (const def of defs) {
    if (!def.key.trim() || !def.expression.trim()) continue;
    try {
      const value = toOutputValue(evaluateExpression(def.expression, scope));
      if (typeof value === 'number' && Number.isNaN(value)) {
        errors[def.key] = 'Expression produced an invalid number (NaN).';
        continue;
      }
      outputs[def.key] = value;
      // Make this output referenceable by subsequent output expressions.
      scope[def.key] = value;
    } catch (e) {
      errors[def.key] = e instanceof Error ? e.message : 'Evaluation failed.';
    }
  }

  return { outputs, errors };
}
